/**
 * ============================================================================
 * LoginPage.js - JWT Login Page Component
 * ============================================================================
 * Handles user authentication with email and password
 * Created: November 27, 2025 - Phase 2C Multi-Tenancy
 * 
 * FEATURES:
 * - Email and password login
 * - Remember me checkbox (persists email)
 * - Forgot password link
 * - Registration link
 * - Error handling and validation
 * - Loading states
 * - Redirect to dashboard on success
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading } = useAuth();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Success message from registration or password reset
  const [successMessage, setSuccessMessage] = useState('');
  
  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
    
    // Check for success message from redirect
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const from = location.state?.from;
      navigate((from?.pathname || '/dashboard') + (from?.search || ''), { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.state]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    // Validate inputs
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!password) {
      setError('Please enter your password');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        // Handle remember me
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email.toLowerCase().trim());
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // Check if org needs Stripe checkout completion
        if (result.setupPending || result.data?.requiresCheckout) {
          // Store tokens temporarily for checkout redirect
          sessionStorage.setItem('pendingAccessToken', result.data?.accessToken || localStorage.getItem('accessToken'));
          sessionStorage.setItem('pendingRefreshToken', result.data?.refreshToken || localStorage.getItem('refreshToken'));
          sessionStorage.setItem('pendingOrgId', result.data?.organization?.id || '');
          
          // Redirect to register page to complete Stripe checkout
          navigate('/register', { 
            state: { 
              message: 'Please complete your subscription setup to access the dashboard.',
              resumeCheckout: true,
              organizationId: result.data?.organization?.id,
              organizationName: result.data?.organization?.name
            },
            replace: true 
          });
          return;
        }
        
        // Redirect to intended destination or dashboard (preserve search params for email deep-links)
        const from = location.state?.from;
        navigate((from?.pathname || '/dashboard') + (from?.search || ''), { replace: true });
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show loading while checking auth
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)'
      }}>
        <div style={{ color: '#fff', fontSize: 18 }}>Loading...</div>
      </div>
    );
  }
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 40,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Logo and Title */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 4px 12px rgba(45, 106, 79, 0.1)',
            padding: 8
          }}>
            <img 
              src="/logo.png" 
              alt="Tee Time Organizer" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
            />
          </div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1b4332' }}>
            Tee Time Organizer
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#6b7280' }}>
            Sign in to your account
          </p>
        </div>
        
        {/* Success Message */}
        {successMessage && (
          <div style={{
            padding: '12px 16px',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 8,
            color: '#166534',
            fontSize: 14,
            marginBottom: 20
          }}>
            {successMessage}
          </div>
        )}
        
        {/* Login Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Email Field */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: 8, 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#374151' 
            }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="you@example.com"
              disabled={isSubmitting}
              autoComplete="email"
              autoFocus={!email}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: error ? '2px solid #ef4444' : '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isSubmitting ? '#f9fafb' : '#fff',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                if (!error) e.target.style.borderColor = '#2d6a4f';
              }}
              onBlur={(e) => {
                if (!error) e.target.style.borderColor = '#e5e7eb';
              }}
            />
          </div>
          
          {/* Password Field */}
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 8 
            }}>
              <label style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151' 
              }}>
                Password
              </label>
              <Link 
                to="/forgot-password"
                style={{
                  fontSize: 13,
                  color: '#2d6a4f',
                  textDecoration: 'none',
                  fontWeight: 500
                }}
              >
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              placeholder="Enter your password"
              disabled={isSubmitting}
              autoComplete="current-password"
              autoFocus={!!email}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: error ? '2px solid #ef4444' : '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isSubmitting ? '#f9fafb' : '#fff',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                if (!error) e.target.style.borderColor = '#2d6a4f';
              }}
              onBlur={(e) => {
                if (!error) e.target.style.borderColor = '#e5e7eb';
              }}
            />
          </div>
          
          {/* Remember Me Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isSubmitting}
              style={{
                width: 18,
                height: 18,
                accentColor: '#2d6a4f',
                cursor: 'pointer'
              }}
            />
            <label 
              htmlFor="rememberMe" 
              style={{ 
                fontSize: 14, 
                color: '#6b7280',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Remember my email
            </label>
          </div>
          
          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: 14
            }}>
              {error}
            </div>
          )}
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '14px',
              background: isSubmitting ? '#9ca3af' : '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: isSubmitting ? 'none' : '0 2px 8px rgba(45, 106, 79, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.target.style.background = '#1b4332';
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.target.style.background = '#2d6a4f';
            }}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        {/* Contact Link - Registration temporarily redirects to contact form until Phase 3 pricing is ready */}
        <div style={{
          marginTop: 24,
          padding: '16px',
          background: '#f9fafb',
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <span style={{ fontSize: 14, color: '#6b7280' }}>
            Don't have an account?{' '}
          </span>
          <Link 
            to="/?landing=true&contact=true"
            style={{
              fontSize: 14,
              color: '#2d6a4f',
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Contact us
          </Link>
        </div>
        
        {/* Security Notice */}
        <div style={{
          marginTop: 16,
          fontSize: 12,
          color: '#9ca3af',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: 4 }}>🔒 Secure Login</div>
          <div>Your data is encrypted and secure</div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
